import { createContext, createElement, type ReactNode, useContext, useState } from 'react';

import { type FileDefinitionValue } from 'app/core/files/types';
import { type EconomyActivity } from 'app/modules/Operations/EconomyActivity/types';
import { type ContactModel, type TaxStep } from 'app/modules/Taxpayer/TaxpayerRequest/steps/types';
import {
  type GeneralTaxpayer,
  type ReportRow,
  type Steps,
  type TaxRow
} from 'app/modules/Taxpayer/TaxpayerRequest/types';
import { create, type StateCreator } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface TaxpayerRequestStore {
  form: Steps;
  contact: ContactModel | {};
  sending: boolean;
  setRequest: (steps: Steps) => void;
  addGeneralData: (data: GeneralTaxpayer) => void;
  addOperationsData: (data: Array<EconomyActivity>) => void;
  addDocumentsData: (data: Array<FileDefinitionValue>) => void;
  addContact: (contact: ContactModel) => void;
  removeContact: (contact: ContactModel) => void;
  setContact: (contact: ContactModel) => void;
  addTax: (tax: TaxStep) => void;
  addTaxPeriodicities: (taxes: Array<TaxRow>) => void;
  addReportPeriodicities: (reports: Array<ReportRow>) => void;
  setSending: (sending: boolean) => void;
  reset: () => void;
}

const initialForm: Steps = {
  contacts: [],
  general: {},
  tax: {} as TaxStep,
  taxes: [],
  reports: [],
  operations: [],
  documents: []
};

const taxpayerRequestStoreState =
  (initialData?: Steps): StateCreator<TaxpayerRequestStore> =>
  (set) => ({
    form: { ...initialForm, ...initialData },
    contact: {},
    sending: false,
    setRequest: (steps) => set({ form: steps }),
    addGeneralData: (data) => set((state) => ({ form: { ...state.form, general: data } })),
    addOperationsData: (data) => set((state) => ({ form: { ...state.form, operations: data } })),
    addDocumentsData: (data) => set((state) => ({ form: { ...state.form, documents: data } })),
    addContact: (contact) =>
      set((state) => {
        const contacts = state.form.contacts ?? [];
        const exists = contacts.find((c) => c.index === contact.index);
        const updated = exists
          ? contacts.map((c) => (c.index === contact.index ? contact : c))
          : [...contacts, contact];
        return { form: { ...state.form, contacts: updated } };
      }),
    removeContact: (contact) =>
      set((state) => ({
        form: {
          ...state.form,
          contacts: state.form.contacts?.filter((c) => c.index !== contact.index)
        },
        contact: {}
      })),
    setContact: (contact) => set({ contact }),
    addTax: (tax) => set((state) => ({ form: { ...state.form, tax } })),
    addTaxPeriodicities: (taxes) =>
      set((state) => ({ form: { ...state.form, taxes: [...taxes] } })),
    addReportPeriodicities: (reports) =>
      set((state) => ({ form: { ...state.form, reports: [...reports] } })),
    setSending: (sending) => set({ sending }),
    reset: () => set({ form: initialForm, contact: {}, sending: false })
  });

const createTaxpayerRequestStore = (initialData?: Steps) =>
  create<TaxpayerRequestStore>()(taxpayerRequestStoreState(initialData));

export const usePublicTaxpayerRequestStore = create<TaxpayerRequestStore>()(
  persist(taxpayerRequestStoreState(), {
    name: 'taxpayer-request-wizard',
    storage: createJSONStorage(() => sessionStorage),
    version: 1
  })
);

const TaxpayerRequestStoreContext = createContext<ReturnType<
  typeof createTaxpayerRequestStore
> | null>(null);

export const TaxpayerRequestStoreProvider = ({
  children,
  initialData
}: {
  children: ReactNode;
  initialData?: Steps;
}) => {
  const [store] = useState(() => createTaxpayerRequestStore(initialData));

  return createElement(TaxpayerRequestStoreContext.Provider, { value: store }, children);
};

export const useTaxpayerRequestStore = <T>(selector: (state: TaxpayerRequestStore) => T) => {
  const store = useContext(TaxpayerRequestStoreContext) ?? usePublicTaxpayerRequestStore;

  return store(selector);
};
